import { IComunicadorAlarma, IDispositivoAlarma } from 'modelos/src';
import { EstadoConexion, minutosDesde } from './estado-conexion.util';

/**
 * Última comunicación de la alarma: la más reciente entre TODOS sus
 * comunicadores (puede haber más de uno — protocolos distintos, ej.
 * principal + backup). Antes era un campo escalar único; ahora hay que
 * mirar el máximo del arreglo. Mismo criterio que usa gestion-datos-go
 * para el conteo de alarmas activas (facturacion.go).
 */
export function ultimaComunicacionAlarma(
  alarma?: Pick<IDispositivoAlarma, 'comunicadores'>,
): string | undefined {
  const fechas = (alarma?.comunicadores ?? [])
    .map((c) => c.fechaUltimaComunicacion)
    .filter((f): f is string => !!f);
  if (!fechas.length) return undefined;
  return fechas.reduce((max, f) => (f > max ? f : max));
}

/**
 * Estado de comunicación con el criterio del módulo de alarmas, el mismo que
 * el backend (dispositivo-Alarmas, calcularReportingAlarma) y el mapa: al día
 * hasta una frecuencia de reporte, demorada hasta dos y sin reportar después.
 * Una alarma manda un keepalive con esa frecuencia: el criterio de trackers
 * (estadoConexion: tres frecuencias y 24 h de mínimo) dejaba una alarma caída
 * como "Demorado" durante un día.
 */
export function estadoComunicacionAlarma(
  fecha: string | undefined,
  frecReporte?: number,
): EstadoConexion {
  const minutos = minutosDesde(fecha);
  if (minutos === null) return { tono: 'neutro', etiqueta: 'Sin reportes' };
  if (!frecReporte) {
    return { tono: 'neutro', etiqueta: 'Sin frecuencia de reporte' };
  }
  if (minutos <= frecReporte) return { tono: 'ok', etiqueta: 'Reportando' };
  if (minutos <= 2 * frecReporte) {
    return { tono: 'advertencia', etiqueta: 'Demorado' };
  }
  return { tono: 'apagado', etiqueta: 'Sin reportar' };
}

/**
 * Estado de la alarma: el de su comunicación más reciente. Como en el
 * backend, con dos o más comunicadores el "al día" pasa a advertencia si
 * alguno no reporta: el backup está para notar cuando falla, no para que el
 * que anda lo tape.
 */
export function estadoConexionAlarma(
  alarma: Pick<IDispositivoAlarma, 'comunicadores' | 'frecReporte'>,
): EstadoConexion {
  const estado = estadoComunicacionAlarma(
    ultimaComunicacionAlarma(alarma),
    alarma.frecReporte,
  );
  const comunicadores = alarma.comunicadores ?? [];
  if (
    estado.tono === 'ok' &&
    comunicadores.length > 1 &&
    comunicadores.some(
      (c) =>
        estadoComunicacionAlarma(c.fechaUltimaComunicacion, alarma.frecReporte)
          .tono !== 'ok',
    )
  ) {
    return { tono: 'advertencia', etiqueta: 'Un comunicador no reporta' };
  }
  return estado;
}

/** El primer comunicador del arreglo (el "principal") — ver comunicadorPorId. */
export function comunicadorPrincipal(
  alarma?: Pick<IDispositivoAlarma, 'comunicadores'>,
): IComunicadorAlarma | undefined {
  return alarma?.comunicadores?.[0];
}
